import React from 'react';
import { $eq } from '../filters';
import { camelCase, startCase } from 'lodash';
import { EntityNameColumn } from '@components/entity-name-column';
import OfflinePinIcon from '@material-ui/icons/OfflinePin';
import { Chip, Tooltip, Typography, Grid, Avatar } from '@material-ui/core';
import { useMemo, useState, useEffect, useRef } from 'react';
import { useSnackbar } from 'notistack';
import { uniq, uniqBy } from 'lodash';
import { ColoredAvatar, ColoredChip } from '@components/colored-elements';
import { orange, blue, deepOrange, pink, indigo, teal, cyan } from '@material-ui/core/colors';
type EnumObject = { label: React.ReactNode; value: any };
type EnumMap = EnumObject[];
import { makeStyles, createStyles, Theme, withStyles } from '@material-ui/core/styles';
import { ListItem, ListItemAvatar, ListItemText } from '@material-ui/core';
import CompanyDefaultIcon from '@icon/company-default';
import { readableDate, toPercentage, usePintMaster, YmdStrToDate } from '@lib/common';
import { format, compareAsc } from 'date-fns';
import formatDistance from 'date-fns/formatDistance';
import differenceInDays from 'date-fns/differenceInDays';
import differenceInSeconds from 'date-fns/differenceInSeconds';
import { useDefaultImage } from '@lib/common';
import { TimeAgo } from '@components/time-ago';
import { TvSeriesIcon } from '@icon/tv-series-icon';
import { MovieIcon } from '@icon/movie-icon';
import { ContentDetail } from '@lib/content';
import { rootCertificates } from 'tls';
import SubtitlesIcon from '@material-ui/icons/Subtitles';
import CopyClipboard from '@material-ui/icons/AttachFile';
import { setYear } from 'date-fns/esm';
import { Link } from '@material-ui/core';
import { title } from 'process';
import { windowObj } from '@lib/common';
import LibraryBooksIcon from '@material-ui/icons/LibraryBooks';
import CastIcon from '@material-ui/icons/Cast';
import SportsEsportsIcon from '@material-ui/icons/SportsEsports';
import BookIcon from '@material-ui/icons/Book';
import MenuBookIcon from '@material-ui/icons/MenuBook';
import BusinessIcon from '@material-ui/icons/Business';
import { FaLeaf } from 'react-icons/fa';
import styled from 'styled-components';
import { COUNTRY_INFO } from 'pages/constants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      display: 'flex',
      '& > *': {
        margin: theme.spacing(1),
      },
    },
    small: {
      width: theme.spacing(3),
      height: theme.spacing(3),
    },
    large: {
      width: theme.spacing(7),
      height: theme.spacing(7),
    },
    orange: {
      color: theme.palette.getContrastText(deepOrange[500]),
      backgroundColor: deepOrange[500],
    },
    avatarImg: {
      width: '40px',
      height: '75px',
      marginRight: '5px',
    },
    containedImage: {
      '& .MuiAvatar-img': {
        objectFit: 'contain',
      },
    },
  })
);
const ContainedAvatar = withStyles({
  img: {
    objectFit: 'contain',
  },
})(Avatar);
/**
 * Convert a string based enum into objects
 * and redable labels.
 *
 * @param enum_ The enum to convert
 */
export const createEnumMap = (enum_: Record<string, any>, retainSpecialChars = false): EnumMap =>
  Object.values(enum_).map(value => ({
    label: retainSpecialChars ? value : startCase(camelCase(value)),
    value,
  }));

/**
 * Render a enum map to Select options.
 * @param enumMap The Enum map
 */
export const EnumsToOptions = (enumMap: EnumMap) => (
  <>
    {enumMap.map(v => (
      <option key={v.value} value={v.value}>
        {v.label}
      </option>
    ))}
  </>
);

/**
 * Override equals operator to show readable
 * values of enums.
 */
export const enumOperatorOverride = {
  [$eq.key]: {
    ChipLabel({ label, value }) {
      return (
        <>
          {label} is {startCase(camelCase(value))}
        </>
      );
    },
  },
};

/**
 * Higher order function to create c
 * @param entity The entity to create object for.
 */
export const cellForEntity =
  (entity: 1 | 2 | '1' | '2') =>
  ({ row }) =>
    (row.original[`entity${entity}`] || row.original[`entity${entity}_vtr_id`]) && (
      <EntityNameColumn
        name={row.original[`entity${entity}`]}
        type={row.original[`entity${entity}_type`]}
        secondary={
          row.original[`entity${entity}_vtr_id`] ? (
            <OfflinePinIcon style={{ fontSize: '1em' }} color="primary" />
          ) : null
        }
      />
    );

/**
 *  function to create cell for content
 * @param entity The entity to create object for.
 */
export const CellForContent = ({ row }) =>
  row.original.content && (
    <EntityNameColumn
      name={row.original.content}
      type="CONTENT"
      secondary={
        row.original.content_vtr_id ? (
          <OfflinePinIcon style={{ fontSize: '1em' }} color="primary" />
        ) : null
      }
    />
  );

export const QuickTitle = ({ title, subTitle = 'Advanced Filters' }) => (
  <>
    <Typography variant="h5">{title}</Typography>
    <Typography variant="body2" color="textSecondary">
      {subTitle}
    </Typography>
    <br />
  </>
);

export const addTitleToFilter =
  (Filter: any, title?: string, subTitle = 'Advanced Filters') =>
  props => {
    const actualTitle = useMemo(
      () => title || props.column.render('Header'),
      [props.column.Header]
    );
    return (
      <>
        <QuickTitle title={actualTitle} subTitle={subTitle} />
        <Filter {...props} />
      </>
    );
  };

export const CellForCompanyTypes = ({ row }) => {
  const max_limit: number = 2;
  const list_of_company_types = row.original.company_types
    ? uniqBy(row.original.company_types, 'name')
    : [];

  const title = list_of_company_types
    .slice(max_limit)
    .map(eachCompanyType => eachCompanyType['name'])
    .join(', ');
  const sliced = list_of_company_types.slice(0, max_limit);
  return (
    row.original.company_types && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
        {sliced.map((eachCompanyType, index) => {
          return (
            <div key={index} style={{ display: 'flex' }}>
              <Tooltip title={eachCompanyType['name']}>
                <ColoredChip
                  colored={blue[500]}
                  size="small"
                  // variant="outlined"
                  label={eachCompanyType['name']}
                />
              </Tooltip>
              {index === sliced.length - 1 && list_of_company_types.length - max_limit > 0 ? (
                <>
                  &nbsp;
                  <Tooltip title={title}>
                    <span style={{ fontWeight: 'bold', paddingLeft: '3px' }}>{`+${
                      list_of_company_types.length - max_limit
                    }`}</span>
                  </Tooltip>
                </>
              ) : null}
            </div>
          );
        })}
      </div>
    )
  );
};

export const CellForDealContentCount = (row, count_param) => {
  return (
    row.original && (
      <div style={{ display: 'flex', flexDirection: 'row' }}>
        {count_param === 'deal_count' ? (
          <Tooltip title={`Deals: ${row.original.deal_count}`}>
            <Link
              href={`${window.location.origin}/deals/search/?filters[0].id=entity_id&filters[0].value.$eq=${row.original.vtr_id}`}
              target="_blank">
              <Typography color="primary" variant="h6">
                {row.original.deal_count}
              </Typography>
            </Link>
          </Tooltip>
        ) : (
          <Tooltip title={`Companies: ${row.original.companies_count}`}>
            <Link
              href={`${window.location.origin}/company-content-beta/search?filters[0].id=content_vtr_id_eq&filters[0].value.$eq=${row.original.vtr_id}`}
              target="_blank">
              <Typography color="secondary" variant="h6">
                {row.original.companies_count}
              </Typography>
            </Link>
          </Tooltip>
        )}
      </div>
    )
  );
};

export const cellForOfficeLocations = ({ row }) => {
  const max_limit: number = 2;
  const listOfOtherOfficeCountries = row.original.other_office_locations
    ? uniq(
        row.original.other_office_locations
          .split('|')
          .map(x => x.split(','))
          .map(x => x[x.length - 1].trim())
      )
    : [];

  const title = listOfOtherOfficeCountries.slice(max_limit).join(', ');
  const sliced = listOfOtherOfficeCountries.slice(0, max_limit);

  return (
    row.original.other_office_locations && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
        {sliced.map((eachOtherOfficeCountry, index) => {
          return (
            <div key={index} style={{ display: 'flex' }}>
              <Tooltip title={eachOtherOfficeCountry}>
                <ColoredChip
                  colored={teal[500]}
                  size="small"
                  // variant="outlined"
                  label={eachOtherOfficeCountry}
                />
              </Tooltip>
              {index === sliced.length - 1 && listOfOtherOfficeCountries.length - max_limit > 0 ? (
                <Tooltip title={title}>
                  <span style={{ fontWeight: 'bold', paddingLeft: '3px' }}>{`+${
                    listOfOtherOfficeCountries.length - max_limit
                  }`}</span>
                </Tooltip>
              ) : null}
            </div>
          );
        })}
      </div>
    )
  );
};

export const CellForCompanyAka = ({ row }) =>
  !!row.original.company_aka && row.original.company_aka;

export const CellForLastModifiedAt = ({ value, row }) => (
  <TimeAgo time={value ? new Date(value) : new Date(row.original.created_at)} breakLine />
);

export const CellForHQCountry = ({ value }) => {
  return (
    <Tooltip title={value}>
      <ColoredChip
        component="p"
        colored={cyan[500]}
        size="small"
        variant="outlined"
        label={value}
      />
    </Tooltip>
  );
};

///////////////////////
export const CellForContentTitle = ({ value, row }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const classes = useStyles();
  let copyvtr_id = false;
  const srcList = useMemo(() => {
    return [
      `${windowObj.RAZZLE_S3_BUCKET_POSTER_URL}/${row.original.vtr_id}.png`,
      `${windowObj.RAZZLE_TMDB_IMAGE_URL}/${row.original.poster_path}`,
      `${row.original.poster_path}`,
      '/images/content_default.svg',
    ].filter(e => e);
  }, []);

  const { src: fallbackedSource } = useDefaultImage({
    srcList,
    defaultSrc: '/images/content_default.svg',
    useSuspense: false,
  });

  return (
    (value || row.original.original_title) && (
      <ListItem style={{ padding: 0 }} component="div">
        <ListItemAvatar style={{ display: 'flex', minWidth: 0, marginRight: 6 }}>
          <Avatar
            variant="square"
            alt={`${row.original.title}`}
            src={fallbackedSource}
            className={[classes.containedImage, classes.avatarImg].join(' ')}
          />
        </ListItemAvatar>
        <ListItemText
          primary={
            <>
              <Typography variant="body2" display="block" style={{ whiteSpace: 'nowrap' }}>
                <ClipBoard
                  parentValue={value ? row.original.title : row.original.original_title}
                  icon={<CopyClipboard style={{ fontSize: '1.2em' }} color="primary" />}
                  value={row.original.vtr_id}
                />
              </Typography>
            </>
          }
          secondary={
            row.original.title !== row.original.original_title &&
            row.original.title &&
            row.original.original_title ? (
              <SmallSubTitle
                name={row.original.original_title}
                icon={<SubtitlesIcon style={{ fontSize: '1em' }} color="primary" />}
              />
            ) : null
          }
        />
        {/* <ListItemText
            primary={
              <Typography variant="caption" display="block">
                {value}
              </Typography>
            }
          /> */}
      </ListItem>
      // </Tooltip>
    )
  );
};

export const NewCellForContentTitle = ({ value, row }) => {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const classes = useStyles();
  let copyvtr_id = false;
  const srcList = useMemo(() => {
    return [
      `${windowObj.RAZZLE_S3_BUCKET_POSTER_URL}/${row.original.vtr_id}.png`,
      `${windowObj.RAZZLE_TMDB_IMAGE_URL}/${row.original.poster_path}`,
      `${row.original.poster_path}`,
      '/images/content_default.svg',
    ].filter(e => e);
  }, []);

  const {imdb_id=""} = row.original || {};
  const {original:{content_type=""}} = row;

  const { src: fallbackedSource } = useDefaultImage({
    srcList,
    defaultSrc: '/images/content_default.svg',
    useSuspense: false,
  });

  return (
    (value || row.original.original_title) && (
      <ListItem style={{ padding: 0 }} component="div">
       <div style={{display:"flex", flexDirection:"column"}}>
           <div style={{display:"flex", alignItems:"center"}}>
          <ListItemAvatar style={{ display: 'flex', minWidth: 0, marginRight: 6 }}>
          <Avatar
            variant="square"
            alt={`${row.original.title}`}
            src={fallbackedSource}
            className={[classes.containedImage, classes.avatarImg].join(' ')}
          />
        </ListItemAvatar>
          <ListItemText
          primary={
            <>
              <Typography variant="body2" display="block" style={{ whiteSpace: 'nowrap' }}>
                <ClipBoard
                  parentValue={value ? row.original.title : row.original.original_title}
                  icon={<CopyClipboard style={{ fontSize: '1em' }} color="primary" />}
                  value={row.original.vtr_id}
                />
              </Typography>
            </>
          }
          secondary={
            row.original.title !== row.original.original_title &&
            row.original.title &&
            row.original.original_title ? (
              <SmallSubTitle
                name={row.original.original_title}
                icon={<SubtitlesIcon style={{ fontSize: '1em' }} color="primary" />}
              />
            ) : null
          }
        />
        </div>
        
        <ContentInfoDiv>
          <CellForContentType value={content_type}/>
          <CellForContentGenres value={value} row={row}/>
          <CellForContentLanguages value={value} row={row}/>
          {imdb_id && <Tooltip title={`IMDB Id: ${imdb_id}`}>
        <a target="_blank" style={{display:"flex"}} href={`https://www.imdb.com/title/${imdb_id}`}>
          <img style={{height:"25px", width:"25px", overflow: "hidden"}} src="/images/imdb.webp"/>
        </a>
      </Tooltip>}
        </ContentInfoDiv>
       </div>
        {/* <ListItemText
            primary={
              <Typography variant="caption" display="block">
                {value}
              </Typography>
            }
          /> */}
      </ListItem>
      // </Tooltip>
    )
  );
};

export enum YesOrNoEnum {
    YES = 'yes',
    NO = 'no',
    NOT_SURE = ""
}

const YesOrNoChip = styled.div`
padding: 10px;
color: white;
border-radius: 30px;
width: fit-content;
`
const ContentInfoDiv = styled.div`
  display:flex;
  gap: 5px;
  align-items:center;

  .MuiGrid-spacing-xs-2 {
    width : fit-content;
  }
`

export const YesOrNoEntity = ({value, title}) => {
  
  const mapping = {
    [YesOrNoEnum.YES]: {
      icon : "✅",
      size: "20px",
      text: "Yes"
    },
    [YesOrNoEnum.NO]: {
      icon : "❌",
      size: "18px",
      text: "No"
    },
    [YesOrNoEnum.NOT_SURE]: {
      icon : "🤔",
      size: "20px",
      text: "Not Sure"
    }
  }
  
  const icon = mapping[value]?.icon || "";
  const size = mapping[value]?.size;
  const text = mapping[value]?.text || "";
  return (
    <Tooltip title={`${title} : ${text}`}>
      <YesOrNoChip style={{fontSize: size}}>
        {icon}
      </YesOrNoChip>
    </Tooltip>
  );
};

export const OriginCountry = ({value})=>{
  const countryIds = value ? value?.split(",") : [];
  const countryNames = COUNTRY_INFO?.filter(({code=""})=> {
    if(countryIds && countryIds?.includes(code)){
      return true;
    }
  })?.map(({name})=> name)?.join(", ");
  return countryNames;
}

export const CellForContentType = ({ value }) => {
  if (value == 0) {
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Tooltip title="Movie">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <MovieIcon style={{ fontSize: '2rem', paddingRight: '5px' }} />
              {/* <Typography variant="body2" component="h6">
                TV Series
              </Typography> */}
            </div>
          </Tooltip>
        </Grid>
      </Grid>
    );
  } else if (value == 1) {
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Tooltip title="TV Series">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <TvSeriesIcon style={{ fontSize: '2rem', paddingRight: '5px' }} />
              {/* <Typography variant="body2" component="h6">
                TV Series
              </Typography> */}
            </div>
          </Tooltip>
        </Grid>
      </Grid>
    );
  }
  if (value == 2) {
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Tooltip title="Book">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <MenuBookIcon style={{ fontSize: '2rem', paddingRight: '5px' }} />
              {/* <Typography variant="body2" component="h6">
                TV Series
              </Typography> */}
            </div>
          </Tooltip>
        </Grid>
      </Grid>
    );
  } else if (value == 3) {
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Tooltip title="Podcast">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <CastIcon style={{ fontSize: '2rem', paddingRight: '5px' }} />
              {/* <Typography variant="body2" component="h6">
                TV Series
              </Typography> */}
            </div>
          </Tooltip>
        </Grid>
      </Grid>
    );
  }
  if (value == 4) {
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Tooltip title="Game">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <SportsEsportsIcon style={{ fontSize: '2rem', paddingRight: '5px' }} />
              {/* <Typography variant="body2" component="h6">
                TV Series
              </Typography> */}
            </div>
          </Tooltip>
        </Grid>
      </Grid>
    );
  } else if (value == 5) {
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Tooltip title="Format">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <LibraryBooksIcon style={{ fontSize: '2rem', paddingRight: '5px' }} />
              {/* <Typography variant="body2" component="h6">
                TV Series
              </Typography> */}
            </div>
          </Tooltip>
        </Grid>
      </Grid>
    );
  } else if (value == 7) {
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Tooltip title="IP">
            <div style={{ display: 'flex', alignItems: 'center' }}>
                    <img
                      src="/images/IP-rev.png"
                      style={{ height: '20px', marginLeft: '5px',marginTop: '5px' }}
                    />
            </div>
          </Tooltip>
        </Grid>
      </Grid>
    );
  }else if (value == 8) {
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Tooltip title="Micro-Series">
            <div style={{ display: 'flex', alignItems: 'center' }}>
             <img
                      src="/images/micro-series.png"
                      style={{ height: '20px', marginLeft: '5px',marginTop: '5px' }}
                    />
            </div>
          </Tooltip>
        </Grid>
      </Grid>
    );
  } else {
    return (
      <Grid container spacing={2} alignItems="center">
        <Grid item>
          <Tooltip title="Story/Script">
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <BookIcon style={{ fontSize: '2rem', paddingRight: '5px' }} />
              {/* <Typography variant="body2" component="h6">
                TV Series
              </Typography> */}
            </div>
          </Tooltip>
        </Grid>
      </Grid>
    );
  }
};

export const CellForContentGenres = ({ value, row }) => {
  const max_limit: number = 1;
  const listOfContentGenres = row.original.genres
    ? uniq(row.original.genres.split(',').map(x => x.trim()))
    : [];

  const title = listOfContentGenres.slice(max_limit).join(', ');
  const sliced = listOfContentGenres.slice(0, max_limit);
  return row.original.genres ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
      {sliced.map((eachGenre, index) => {
        return (
          <div key={index} style={{ display: 'flex' }}>
            <Tooltip title={eachGenre}>
              <ColoredChip
                colored={teal[500]}
                size="small"
                // variant="outlined"
                label={eachGenre}
              />
            </Tooltip>
            {index === sliced.length - 1 && listOfContentGenres.length - max_limit > 0 ? (
              <Tooltip title={title}>
                <span style={{ fontWeight: 'bold', paddingLeft: '3px' }}>{`+${
                  listOfContentGenres.length - max_limit
                }`}</span>
              </Tooltip>
            ) : null}
          </div>
        );
      })}
    </div>
  ) : null;
};

export const CellForContentCountryNames = ({
  value,
  row,
}: {
  value: string;
  row: { [key: string]: ContentDetail };
}) => {
  const max_limit: number = 2;
  const listOfContentCountryNames = row.original.country_names
    ? uniq(row.original.country_names.split(',').map(x => x.trim()))
    : [];

  const title = listOfContentCountryNames.slice(max_limit).join(', ');
  const sliced = listOfContentCountryNames.slice(0, max_limit);
  return (
    row.original.country_names && (
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
        {sliced.map((eachCountryName, index) => {
          return (
            <div key={index} style={{ display: 'flex' }}>
              <Tooltip title={eachCountryName}>
                <ColoredChip
                  colored={blue[500]}
                  size="small"
                  // variant="outlined"
                  label={eachCountryName}
                />
              </Tooltip>
              {index === sliced.length - 1 && listOfContentCountryNames.length - max_limit > 0 ? (
                <Tooltip title={title}>
                  <span style={{ fontWeight: 'bold', paddingLeft: '3px' }}>{`+${
                    listOfContentCountryNames.length - max_limit
                  }`}</span>
                </Tooltip>
              ) : null}
            </div>
          );
        })}
      </div>
    )
  );
};

export const CellForContentLanguages = ({
  value,
  row,
}: {
  value: string;
  row: { [key: string]: ContentDetail };
}) => {
  const max_limit: number = 1;
  const listOfContentLanguages = row.original.lang_names
    ? uniq(row.original.lang_names.split(',').map(x => x.trim()))
    : [];

  const title = listOfContentLanguages.slice(max_limit).join(', ');
  const sliced = listOfContentLanguages.slice(0, max_limit);
  return row.original.lang_names ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
      {sliced.map((eachLanguage, index) => {
        return (
          <div key={index} style={{ display: 'flex' }}>
            <Tooltip title={eachLanguage}>
              <ColoredChip
                colored={deepOrange[800]}
                size="small"
                // variant="outlined"
                label={eachLanguage}
              />
            </Tooltip>
            {index === sliced.length - 1 && listOfContentLanguages.length - max_limit > 0 ? (
              <Tooltip title={title}>
                <span style={{ fontWeight: 'bold', paddingLeft: '3px' }}>{`+${
                  listOfContentLanguages.length - max_limit
                }`}</span>
              </Tooltip>
            ) : null}
          </div>
        );
      })}
    </div>
  ) : null;
};



export const CellForContentSource = ({
  value,
  row,
}: {
  value: string;
  row: { [key: string]: ContentDetail };
}) => {
  const max_limit: number = 1;
  // console.log({"row_original": row.original})
  var source_label = 'NA'
  if(row?.original?.datasource_id == 4001){
    source_label = "DELI"
  }
  else if(row?.original?.datasource_id == 1001){
    source_label = "TMDB"
  }
  else if(row?.original?.datasource_id == 5001){
    source_label = "Seller Catalogue"
  }
  else if(row?.original?.datasource_id == 2001){
    source_label = "OMDB"
  }
  else if(row?.original?.datasource_id == 3001){
    source_label = "GVD"
  }
  return row.original.datasource_id ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
          <div style={{ display: 'flex' }}>
              <Tooltip title={source_label}>
              <ColoredChip
                colored={blue[800]}
                size="small"
                // variant="outlined"
                label={source_label}
              />
            </Tooltip>
           
          </div>
    </div>
  ) : null;
};

export const CellForReleaseYear = ({
  value,
  row,
}: {
  value: string;
  row: { [key: string]: ContentDetail };
}) => {
  const max_limit: number = 1;
  const listOfReleaseYear = row.original.release_date
    ? uniq(row.original.release_date.split(',').map(x => x.trim()))
    : [];
  const r_year = parseInt(listOfReleaseYear[0]);

  let year: string | number | undefined = row.original.release_year;
  if (year === undefined || year === null || year === "") {
    year = r_year;
  }
  // If year is not a valid number, set to blank
  if (year === undefined || year === null || year === "" || isNaN(Number(year))) {
    return null;
  }
  year = String(year);

  return year ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
      <Tooltip title={year}>
        <ColoredChip
          colored={pink[800]}
          size="small"
          // variant="outlined"
          label={year}
        />
      </Tooltip>
    </div>
  ) : null;
};

export const CellForProductionYear = ({
  value,
  row,
}: {
  value: string;
  row: { [key: string]: ContentDetail };
}) => {
  const max_limit: number = 1;
  const listOfProductionYear = row.original.production_year;
  // console.log(listOfProductionYear);
  // const year = parseInt(listOfProductionYear);
  // const title = listOfReleaseYear.slice(max_limit).join(', ');
  // const sliced = listOfReleaseYear.slice(0, max_limit);
  return row.original.production_year ? (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '3px' }}>
      <Tooltip title={listOfProductionYear}>
        <ColoredChip
          colored={indigo[800]}
          size="small"
          // variant="outlined"
          label={listOfProductionYear}
        />
      </Tooltip>
    </div>
  ) : null;
};
/**
 * Create a small subtitle, used for Original title.
 */
function SmallSubTitle({ name = null, icon = null, display = 'block' }) {
  return (
    <span style={{ color: 'inherit', fontSize: 11, display, alignItems: 'center' }}>
      {icon}&nbsp;{name}
    </span>
  );
}

/**
 * Render a Clipboard Icon to copy the Vitrina ID.
 */
function ClipBoard({ parentValue, icon, display = 'block', value }) {
  const [isHovering, setHovering] = useState(false);
  const [isClick, setClick] = useState(false);
  return (
    <>
      <span
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => {
          setClick(false);
          setTimeout(() => {
            setHovering(false);
          }, 5000);
        }}>
        {parentValue}
      </span>
      <span
        style={{
          color: 'inherit',
          fontSize: 11,
          display: isHovering ? 'inline' : 'none',
          alignItems: 'center',
          paddingTop: '2px',
        }}>
        <Tooltip
          title={isClick ? 'Copied!!' : 'Copy the Vitrina Id'}
          // arrow
          // followCursor
          onClick={() => {
            navigator.clipboard.writeText(value);
            setClick(true);
          }}>
          {icon}
        </Tooltip>
      </span>
    </>
  );
}

// : (
//   <>
//     <span onMouseEnter={() => setHovering(true)}>Always Present</span>
//     <span
//       style={{
//         color: 'inherit',
//         fontSize: 11,
//         display: isHovering ? 'block' : 'none',
//         alignItems: 'center',
//       }}>
//       <Tooltip
//         title={isClick ? 'Copied!!' : 'Copy the Vitrina Id'}
//         // arrow
//         // followCursor
//         onClick={() => {
//           navigator.clipboard.writeText(row.original.vtr_id);
//           setClick(true);
//         }}>
//         {iconcopy}
//       </Tooltip>
//     </span>
//   </>
// );
// const [isHovering, setHovering] = useState(false);
// const [isClick, setClick] = useState(false);
// function handleMouseEnter(e) {
//   setHovering(true);
// }
// function handleMouseLeave(e) {
//   setHovering(false);
// }
{
  /* <Tooltip
          title={copyvtr_id ? 'Vtr Id copied' : 'Click to copy the Vitrina Id'}
          arrow
          placement="bottom"
          onClick={() => {
            navigator.clipboard.writeText(row.original.vtr_id);
            copyvtr_id = true;
            enqueueSnackbar('Vitrina Id copied successfully', { variant: 'success' });
            // title={'Vtr id copied successfully'}
          }}>
          <ClipBoard icon={<CopyClipboard style={{ fontSize: '1em' }} color="primary" />} />
        </Tooltip> */
}
